import {useFormContext} from "react-hook-form";

import {Alert} from "@/components/alert/Alert.tsx";
import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {RadioField} from "@/components/form/fields/RadioField/RadioField.tsx";
import {
  MonthlyRepeatMode,
  Weekday,
  WeekdayDisplay,
} from "@/pages/maintenance/maintenance-create/tabs/general/MaintenanceScheduleGeneralTab.types.ts";
import {
  getDayOrdinal,
  getMonthlyRecurrenceParts,
  getOrdinal,
} from "@/pages/maintenance/maintenance-create/tabs/general/subComponents/MonthlyRepeatOptions/MonthlyRepeatOptions.utils.ts";

export const MonthlyRepeatOptions = () => {
  const {watch} = useFormContext();
  const {user} = useCurrentUserContext();
  const timezone = user!.account!.timezone;
  const startDate: Date | undefined = watch("start_date");

  if (!startDate) {
    return (
      <div className="pulseTextBodySmall text-muted">Select a start date to configure monthly repeat options.</div>
    );
  }

  const {dayOfMonth, jsWeekday, occurrence, isLast} = getMonthlyRecurrenceParts(startDate, timezone);
  const weekday = Object.values(Weekday)[jsWeekday];
  const weekdayName = WeekdayDisplay[weekday];
  const ordinal = isLast ? "last" : getOrdinal(occurrence);
  const showDayWarning = dayOfMonth >= 29;

  return (
    <div className={"d-flex flex-column gap-1"}>
      <span className="pulseTextBodySmallBold">Repeat on</span>
      <div className={"d-flex flex-column gap-3"}>
        <RadioField
          name="monthly_repeat_mode"
          variant={"input"}
          subtextVariant={"checkbox"}
          value={MonthlyRepeatMode.DAY_OF_MONTH}
          label={`Day ${dayOfMonth} of every month`}
          helpText={`Runs on the ${getDayOrdinal(dayOfMonth)} when available.`}
        />
        {showDayWarning && (
          <Alert callout={"Attention"} variant="maintenance" size="small">
            For months with fewer than {dayOfMonth} days, Maintenance will run on the last day of the month.
          </Alert>
        )}

        <RadioField
          name="monthly_repeat_mode"
          variant={"input"}
          subtextVariant={"checkbox"}
          value={MonthlyRepeatMode.NTH_WEEKDAY}
          label={`The ${ordinal} ${weekdayName} of every month`}
          helpText={`Runs on the ${ordinal} ${weekdayName}, regardless of date.`}
        />
      </div>
    </div>
  );
};
